import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { a as createLogger, i as isTriggerEvent, e as emitEvent, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$1 } from './cl-typography2.js';

// Constants
const component = 'cl-dropdown-item';

const clDropdownItemCss = ".sc-cl-dropdown-item-h{align-items:center;border:var(--border-width) solid transparent;cursor:pointer;display:flex;inline-size:-moz-max-content;inline-size:max-content;line-height:1;outline:none;padding:calc(var(--spacing-1) - var(--border-width)) var(--spacing-2) calc(var(--spacing-1) - var(--border-width)) var(--spacing-1);visibility:inherit;width:100%}.sc-cl-dropdown-item-h .dropdown-item__label.sc-cl-dropdown-item{order:2;vertical-align:middle}.sc-cl-dropdown-item-h .dropdown-item__subtext.sc-cl-dropdown-item{margin-left:var(--spacing-1);order:3;vertical-align:middle}.sc-cl-dropdown-item-h:focus-visible{border:var(--border-width) solid rgb(var(--system-color-focus));border-radius:var(--border-radius-large)}.sc-cl-dropdown-item-h:hover.sc-cl-dropdown-item-s>[slot=leading],.sc-cl-dropdown-item-h:hover.sc-cl-dropdown-item-s>[slot=trailing]{color:rgba(var(--color-brand-600))}.sc-cl-dropdown-item-h:hover .dropdown-item__label.sc-cl-dropdown-item{--typography-color:rgba(var(--font-color-onlight-brand))}.sc-cl-dropdown-item-h:hover .dropdown-item__subtext.sc-cl-dropdown-item{--typography-color:rgba(var(--font-color-onlight-brand))}.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>[slot=leading],.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>[slot=trailing]{--icon-size:calc(var(--size-2) + var(--size-half)) !important;--status-size:calc(var(--size-2) + var(--size-half)) !important;color:rgb(var(--color-neutral-900));display:inline-block;margin-right:var(--spacing-1);padding:0;vertical-align:middle}.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>[slot=leading]>*,.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>[slot=trailing]>*{--icon-size:calc(var(--size-2) + var(--size-half)) !important;--status-size:calc(var(--size-2) + var(--size-half)) !important}.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>[slot=leading]{order:1}.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>[slot=trailing]{order:4}.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>div[slot=leading],.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>span[slot=leading],.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>div[slot=trailing],.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>span[slot=trailing]{align-items:center;display:flex;justify-content:center;margin:0;position:relative}.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>div[slot=leading]>:last-child,.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>span[slot=leading]>:last-child{margin-right:var(--spacing-1)}.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>div[slot=trailing]>:first-child,.sc-cl-dropdown-item-h.sc-cl-dropdown-item-s>span[slot=trailing]>:first-child{margin-left:var(--spacing-1)}.sc-cl-dropdown-item-h.dropdown-item--checkbox.sc-cl-dropdown-item-s>cl-checkbox[slot=leading],.sc-cl-dropdown-item-h.dropdown-item--checkbox.sc-cl-dropdown-item-s>[slot=leading]>cl-checkbox{padding:var(--spacing-quarter)}.sc-cl-dropdown-item-h.dropdown-item--selected:not(.dropdown-item--checkbox):not(.dropdown-item--disabled).sc-cl-dropdown-item-s>[slot=leading],.sc-cl-dropdown-item-h.dropdown-item--selected:not(.dropdown-item--checkbox):not(.dropdown-item--disabled).sc-cl-dropdown-item-s>[slot=trailing]{color:rgba(var(--color-brand-600))}.dropdown-item--selected.sc-cl-dropdown-item-h:not(.dropdown-item--checkbox):not(.dropdown-item--disabled) .dropdown-item__label.sc-cl-dropdown-item{--typography-color:rgba(var(--font-color-onlight-brand))}.dropdown-item--selected.sc-cl-dropdown-item-h:not(.dropdown-item--checkbox):not(.dropdown-item--disabled) .dropdown-item__subtext.sc-cl-dropdown-item{--typography-color:rgba(var(--font-color-onlight-brand))}.dropdown-item--disabled.sc-cl-dropdown-item-h{pointer-events:none}.sc-cl-dropdown-item-h.dropdown-item--disabled.sc-cl-dropdown-item-s>[slot=leading],.sc-cl-dropdown-item-h.dropdown-item--disabled.sc-cl-dropdown-item-s>[slot=trailing]{color:rgba(var(--font-color-onlight-muted))}.dropdown-item--disabled.sc-cl-dropdown-item-h .dropdown-item__label.sc-cl-dropdown-item,.dropdown-item--disabled.sc-cl-dropdown-item-h .dropdown-item__subtext.sc-cl-dropdown-item{cursor:not-allowed;--typography-color:rgba(var(--font-color-onlight-muted))}";

const ClDropdownItem = /*@__PURE__*/ proxyCustomElement(class ClDropdownItem extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clSelect = createEvent(this, "clSelect", 7);
    this.select = createEvent(this, "select", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * A mutation observer to detect when a slotted element is added/removed to/from the DOM
     */
    this.mutationObserver = new MutationObserver((mutationsList) => mutationsList.forEach((mutationRecord) => {
      if (mutationRecord.type === 'childList') {
        const { addedNodes, removedNodes } = mutationRecord;
        const nodeIsAdded = !!addedNodes.length;
        this.handleMutation(nodeIsAdded ? addedNodes[0] : removedNodes[0], nodeIsAdded);
      }
    }));
    /**
     * Function that handles setting/removing the checkboxElement based on whether it has been removed from the DOM or not
     */
    this.handleMutation = (mutatedElement, isAdded) => {
      if ((mutatedElement === null || mutatedElement === void 0 ? void 0 : mutatedElement.nodeName) === 'CL-CHECKBOX') {
        if (isAdded && this.checkboxElement !== mutatedElement) {
          this.updateCheckboxElement(mutatedElement);
          this.element.classList.add('dropdown-item--checkbox');
          this.checkboxElement = mutatedElement;
        }
        else if (!isAdded && this.checkboxElement === mutatedElement) {
          this.element.classList.remove('dropdown-item--checkbox');
          this.checkboxElement = undefined;
        }
      }
    };
    /**
     * Handles emitting the `select` when the dropdown-item has been triggered.
     */
    this.handleTrigger = (event) => {
      if (isTriggerEvent(event)) {
        event.preventDefault();
        emitEvent(this, 'clSelect', { component, originalEvent: event, label: this.label, value: this.value }, { label: this.label, value: this.value });
      }
    };
    /**
     * Function that updates the attributes of the slotted checkbox element
     */
    this.updateCheckboxElement = (checkboxElement) => {
      var _a;
      const buttonElement = (_a = checkboxElement.getElementsByTagName('button')) === null || _a === void 0 ? void 0 : _a[0];
      checkboxElement.setAttribute('checked', `${this.selected}`);
      checkboxElement.setAttribute('disabled', `${this.disabled}`);
      checkboxElement.setAttribute('small', 'true');
      if (buttonElement) {
        buttonElement.setAttribute('tabindex', '-1');
        buttonElement.style.setProperty('pointer-events', 'none');
      }
    };
    this.disabled = false;
    this.label = '';
    this.selected = false;
    this.subtext = '';
    this.value = '';
  }
  /**
   * Validates whether a label is given or not
   */
  validateLabel(label) {
    if (!label.length) {
      this.logger.error.attribute.label(`Given invalid value "${label}".`, 'A label is required for the dropdown-item in order to be selectable.');
    }
  }
  /**
   * Validates whether a value is given or not
   */
  validateValue(value) {
    if (!value.length) {
      this.logger.error.attribute.value(`Given invalid value "${value}".`, 'A value is required for the dropdown-item in order to be selectable.');
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate the label attribute;
   * - Validate the value attribute;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'select', 'clSelect');
    this.validateLabel(this.label);
    this.validateValue(this.value);
  }
  /**
   * Every time the component is disconnected from the DOM:
   * - Disconnect the mutation observer;
   */
  disconnectedCallback() {
    this.mutationObserver.disconnect();
  }
  /**
   * Just once before the component has fully loaded and rendered:
   * - Add a mutation observer to the dropdown-item element;
   * - Look for a slotted checkbox element and call the `handleMutation` method if a checkbox element is found;
   */
  componentWillLoad() {
    this.mutationObserver.observe(this.element, { childList: true, subtree: true });
    const initialCheckboxElement = this.element.querySelector('cl-checkbox[slot="leading"]') ||
      this.element.querySelector('[slot="leading"] > cl-checkbox');
    if (initialCheckboxElement) {
      this.handleMutation(initialCheckboxElement, !!initialCheckboxElement);
    }
  }
  /**
   * Before every render:
   * - Update the attributes of the slotted checkbox element;
   */
  componentWillRender() {
    if (this.checkboxElement) {
      this.updateCheckboxElement(this.checkboxElement);
    }
  }
  render() {
    const { disabled, label, selected, subtext } = this;
    return (h(Host, { class: cl('dropdown-item', {
        'dropdown-item--disabled': disabled,
        'dropdown-item--selected': selected,
      }), role: 'listitem', tabIndex: -1, onClick: this.handleTrigger, onKeyPress: this.handleTrigger }, h("slot", { name: 'leading' }), label && (h("cl-typography", { class: 'dropdown-item__label', variant: 'body-2' }, label)), subtext && (h("cl-typography", { class: 'dropdown-item__subtext', type: 'disabled', variant: 'body-2' }, subtext)), h("slot", { name: 'trailing' })));
  }
  get element() { return this; }
  static get watchers() { return {
    "label": ["validateLabel"],
    "value": ["validateValue"]
  }; }
  static get style() { return clDropdownItemCss; }
}, [6, "cl-dropdown-item", {
    "disabled": [4],
    "label": [1],
    "selected": [4],
    "subtext": [1],
    "value": [1]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-dropdown-item", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-dropdown-item":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClDropdownItem);
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClDropdownItem as C, defineCustomElement as d };

//# sourceMappingURL=cl-dropdown-item2.js.map